import React, { createContext, useContext, useEffect, useRef } from 'react';

import { MenuList, ClickAwayListener, Paper, Popper, Grow } from '@material-ui/core';
import { setFilterMenuOpened } from 'actions/filtersMenu';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { getRandomInt, isDatePickerClicked, isTransientElementClicked } from 'constants/helper';

/*

  CustomMenu is the main function, you can just use that or you can
  use dropdown list component as well, it has autofocus functionality
  which helps make the dropdown work with keys.
  Every component expects children as input to keep things modular
  
  You can provide icon to CustomMenu, that is how your display would look like
*/

export const DropdownContext = createContext();

export function Dropdown({ className, children, disablePortal = false, paperStyles }) {
  const { anchorRef, handleClickAway, isOpen } = useContext(DropdownContext);
  const minWidth = anchorRef.current ? anchorRef.current.clientWidth : '20%';
  return (
    <StyledPopper
      disablePortal={disablePortal}
      className={className}
      open={isOpen}
      anchorEl={anchorRef.current}
      role={undefined}
      transition
    >
      {({ TransitionProps, placement }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <Paper style={{ marginTop: '0.5rem', minWidth, ...paperStyles }}>{children}</Paper>
          </ClickAwayListener>
        </Grow>
      )}
    </StyledPopper>
  );
}

// This has autofocus functionality, because of which keydown works
export function DropdownList({ className, children }) {
  const { isOpen } = useContext(DropdownContext);
  return (
    <MenuList autoFocusItem={isOpen} className={className}>
      {children}
    </MenuList>
  );
}

function CustomMenu({
  menuId,
  icon,
  children,
  className,
  onOpen,
  menuType,
  onClickAway,
  disablePortal = false,
  paperStyles = null,
  onClick = () => {},
}) {
  const anchorRef = useRef();
  const [isOpen, setOpen] = React.useState(false);
  const dispatch = useDispatch();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    onClick();
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // here used to check if datepicker is clicked, for both cases, if datepicker is still in DOM, or it has been removed when the control reaches here.
  const handleClickAway = (e) => {
    if (isDatePickerClicked(e) || isTransientElementClicked(e)) return;
    handleClose(e);
    if (typeof onClickAway === 'function') {
      onClickAway();
    }
  };

  useEffect(() => {
    if (isOpen && typeof onOpen === 'function') {
      onOpen();
    }

    if (menuType) {
      dispatch(setFilterMenuOpened(menuType, isOpen));
    }
  }, [isOpen]);

  return (
    <DropdownContext.Provider value={{ anchorRef, handleClose, handleClickAway, isOpen, onClickAway }}>
      {React.cloneElement(icon, { onClick: handleToggle, ref: anchorRef })}
      <Dropdown className={className} disablePortal={disablePortal} paperStyles={paperStyles}>
        {children}
      </Dropdown>
    </DropdownContext.Provider>
  );
}

export default CustomMenu;

const StyledPopper = styled(Popper)`
  max-width: ${(props) => props.maxWidth};
  li {
    white-space: inherit;
  }
  min-width: 5rem;
  z-index: 999;
`;

CustomMenu.defaultProps = {
  menuId: `simpleMenu${getRandomInt()}${getRandomInt()}`,
};

CustomMenu.propTypes = {
  menuId: PropTypes.string,
  icon: PropTypes.element.isRequired,
};

CustomMenu.DropdownList = DropdownList;
